import { useState } from 'react';
import Modal from '../../modal/Modal';
import { BUTTON_CONSTANTS, LABEL_CONSTANTS, TABLE_HEADER_CONSTANTS } from '../../../constants/common';
import SelectStorageItems from '../../select-storage-items/SelectStorageItems';
import { StorageTankOptions } from '../../../types/enums';
import { Account, selectOption, StrawRow } from '../../../types/interfaces';
import AccountSearch from '../../custom-input/AccountSearch';

type AddEmbryoStrawModalProps = {
  isOpen: boolean;
  setIsOpen: (e: boolean) => void;
  onConfirm: (rows: StrawRow[]) => void;
  specimenId: number;
};

const AddEmbryoStrawModal = ({ isOpen, setIsOpen, specimenId, onConfirm }: AddEmbryoStrawModalProps) => {
  const initialState = { value: '', name: '' };

  const [site, setSite] = useState<selectOption>(initialState);
  const [tank, setTank] = useState<selectOption>(initialState);
  const [canister, setCanister] = useState<selectOption>(initialState);
  const [selectedAccount, setSelectedAccount] = useState<Account | undefined>(undefined);

  const [strawRows, setStrawRows] = useState<StrawRow[]>([
    { embryoStrawNumber: '', embryoGrade: '', embryoStage: '', embryoCountPerStraw: 0 },
  ]);

  const handleInputChange = (index: number, field: keyof StrawRow, value: string | number) => {
    setStrawRows(prev =>
      prev.map((row, i) => {
        if (i !== index) return row;

        if (field === 'embryoCountPerStraw') {
          return { ...row, [field]: Number(value) };
        } else {
          return { ...row, [field]: String(value) };
        }
      }),
    );
  };

  const isFormValid = () => {
    if (!canister.value || !selectedAccount?.accountId) return false;

    return strawRows.every(row => row.embryoStrawNumber.trim() && row.embryoCountPerStraw > 0);
  };

  const handleConfirm = () => {
    const rows = strawRows.map((row: StrawRow) => ({
      embryoStrawNumber: row.embryoStrawNumber,
      embryoGrade: row.embryoGrade,
      embryoStage: row.embryoStage,
      embryoCountPerStraw: row.embryoCountPerStraw,
      specimenId,
      accountId: selectedAccount?.accountId,
      storageCanisterId: Number(canister.value),

      storageCanister: {
        name: canister.name,
        storageCanisterId: Number(canister.value),

        storageTankId: Number(tank.value),
        storageTank: {
          name: tank.name,

          storageSiteId: Number(site.value),
          storageSite: {
            name: site.name,
          },
        },
      },
    }));

    onConfirm(rows);
    setIsOpen(false);
  };

  const handleCancel = () => setIsOpen(false);

  return (
    <Modal isOpen={isOpen} onClose={handleCancel} width={700}>
      <div className="add-location-modal">
        <div className="header">
          <h4>{LABEL_CONSTANTS.EMBRYO_STRAWS}</h4>
        </div>

        <div className="body">
          {strawRows.map((row, index) => (
            <div className="select-storage-items">
              <div className="form-row">
                <label>{TABLE_HEADER_CONSTANTS.STRAW_NUMBER}</label>
                <div className="input-container">
                  <input
                    type="text"
                    value={row.embryoStrawNumber}
                    placeholder="Straw Number"
                    onChange={e => handleInputChange(index, 'embryoStrawNumber', e.target.value)}
                  />
                </div>
              </div>

              <div className="form-row">
                <label>{TABLE_HEADER_CONSTANTS.EMBRYO_GRADE}</label>
                <div className="input-container">
                  <input
                    type="text"
                    value={row.embryoGrade}
                    placeholder="Grade"
                    onChange={e => handleInputChange(index, 'embryoGrade', e.target.value)}
                  />
                </div>
              </div>

              <div className="form-row">
                <label>{TABLE_HEADER_CONSTANTS.EMBRYO_STAGE}</label>
                <div className="input-container">
                  <input
                    type="text"
                    value={row.embryoStage}
                    placeholder="Stage"
                    onChange={e => handleInputChange(index, 'embryoStage', e.target.value)}
                  />
                </div>
              </div>

              <div className="form-row">
                <label>{TABLE_HEADER_CONSTANTS.EMBRYO_COUNT}</label>
                <div className="input-container">
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder="0"
                    value={row.embryoCountPerStraw}
                    onChange={e => {
                      const numericValue = e.target.value.replace(/[^0-9]/g, '');
                      handleInputChange(index, 'embryoCountPerStraw', numericValue === '' ? 0 : Number(numericValue));
                    }}
                  />
                </div>
              </div>

              <div className="form-row">
                <label>{TABLE_HEADER_CONSTANTS.ACCOUNT}</label>
                <div className="input-container">
                  <AccountSearch onChange={acc => setSelectedAccount(acc)} placeholder="Search Account" />
                </div>
              </div>
            </div>
          ))}

          <SelectStorageItems
            selectedSite={site}
            selectedTank={tank}
            selectedCanister={canister}
            setSelectedSite={setSite}
            setSelectedTank={setTank}
            setSelectedCanister={setCanister}
            storageTankOptions={StorageTankOptions.Both}
          />
        </div>

        <div className="footer">
          <button className="button blue inverted medium" onClick={handleCancel}>
            {BUTTON_CONSTANTS.CANCEL}
          </button>

          <button type="button" className="button green medium" disabled={!isFormValid()} onClick={handleConfirm}>
            {BUTTON_CONSTANTS.ADD}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default AddEmbryoStrawModal;
